import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css } from 'styled-components';
import { BorderRadiusMedium, BoxShadowLarge, Spacing4 } from 'autogen/design-tokens/tokens';
import {
    type ContentfulPage,
    type ContentfulPreviewUserType,
    ExitPreview,
    SetPreviewUserType,
    SharePreview,
    type SharePreviewParams,
    type SharePreviewSuccess
} from 'autogen/swagger/all';
import { Button } from 'components/Button';
import Icon from 'components/Icon';
import Select from 'components/shared/inputs/select';
import useSegmentation from 'hooks/user/useSegmentation';
import type { IOption } from 'modules/react-select';
import { zIndex } from 'variables';

type SharedUserType = Extract<ContentfulPreviewUserType, 'LoggedIn' | 'Default'>;
type MatasUserType = Extract<ContentfulPreviewUserType, 'ClubMatasPlus'> | SharedUserType;
type KicksUserType = Extract<ContentfulPreviewUserType, 'Silver' | 'Gold'> | SharedUserType;

type UserTypeOption = Omit<IOption, 'value'> & { value: ContentfulPreviewUserType };
type SharedOption = Omit<IOption, 'value'> & { value: SharedUserType };
type MatasUserTypeOption = Omit<IOption, 'value'> & { value: MatasUserType };
type KicksUserTypeOption = Omit<IOption, 'value'> & { value: KicksUserType };

const userTypeOptions: UserTypeOption[] = [
    { value: 'Default', label: 'Default' },
    { value: 'LoggedIn', label: 'Logged in' },
    { value: 'ClubMatasPlus', label: 'Plus' },
    { value: 'Gold', label: 'Gold' },
    { value: 'Silver', label: 'Silver' }
] as const;

const sharedOptions: SharedOption[] = [
    { value: 'Default', label: 'Default' },
    { value: 'LoggedIn', label: 'Logged in' }
];

const matasOptions: MatasUserTypeOption[] = [{ value: 'ClubMatasPlus', label: 'Plus' }, ...sharedOptions];

const kicksOptions: KicksUserTypeOption[] = [
    { value: 'Gold', label: 'Gold' },
    { value: 'Silver', label: 'Silver' },
    ...sharedOptions
];

type PreviewOverlayProps = {
    slug: ContentfulPage['slug'];
    userType?: ContentfulPreviewUserType;
};

const Overlay = styled.div<{ $isVisible: boolean }>`
    --background-color: #e4e49c;
    --transition-time: 1s;
    --transition-curve: cubic-bezier(0.190, 1.000, 0.220, 1.000);
    position: fixed;
    top: 100px;
    width: 100%;
    max-width: 200px;
    background-color: var(--background-color);
    padding: 16px;
    border-top-right-radius: ${BorderRadiusMedium};
    border-bottom-right-radius: ${BorderRadiusMedium};
    left: 0;
    transform: ${({ $isVisible }) => ($isVisible ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform var(--transition-time) var(--transition-curve);
    box-shadow: ${BoxShadowLarge};
    z-index: ${zIndex.dialog};

    @starting-style {
        transform: translateX(-100%);
    }
`;

const Content = styled.div<{ $isVisible: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 8px;
    transition: opacity var(--transition-time) var(--transition-curve);

    ${({ $isVisible }) =>
        $isVisible
            ? css`
                opacity: 1;
            `
            : css`
                opacity: 0;
                pointer-events: none;
            `};

    @starting-style {
        opacity: 0;
    }
`;

const StyledSelect = styled(Select)`
    width: 100%;
    margin-top: ${Spacing4};
`;

const StyledButton = styled(Button)`
    margin-top: ${Spacing4};
    width: 100%;
`;

const HideButtonContainer = styled.div<{ $isVisible: boolean }>`
    transition: transform var(--transition-time) var(--transition-curve);
    transform: ${({ $isVisible }) => ($isVisible ? 'translateX(0)' : 'translateX(100%)')};
    position: absolute;
    top: 0;
    right: 0;
    border-top-right-radius: ${BorderRadiusMedium};
    border-bottom-right-radius: ${BorderRadiusMedium};
    overflow: hidden;
    background-color: var(--background-color);
`;

const Arrow = styled(Icon)<{ $isVisible: boolean }>`
    transition: transform 500ms ease;
    transform: ${({ $isVisible }): string => ($isVisible ? 'scaleX(1)' : ' scaleX(-1)')};
`;

export default function PreviewOverlay(props: PreviewOverlayProps) {
    const { slug, userType = 'Default' } = props;
    const [sharedUrl, setSharedUrl] = useState<string | null>(null);
    const { tenant } = useSegmentation();
    const [availableOptions, setAvailableOptions] = useState<UserTypeOption[]>([]);
    const [selectedOption, setSelectedOption] = useState<UserTypeOption>(
        userTypeOptions.find((x) => x.value === userType) || userTypeOptions[0]
    );
    const [isVisible, setIsVisible] = useState(true);
    const previewOverlayRef = useRef<HTMLDivElement>(null);

    const [hasMounted, setHasMounted] = useState(false);
    const [isInIframe, setIsInIframe] = useState(true);

    useEffect(() => {
        if (tenant === 'Matas') {
            setAvailableOptions(matasOptions);
        }

        if (tenant === 'Kicks') {
            setAvailableOptions(kicksOptions);
        }
    }, [tenant]);

    const onChange = async (option: IOption) => {
        const userTypeOption = option as UserTypeOption;
        const newUserType = userTypeOption.value;
        setSelectedOption(userTypeOption);
        await SetPreviewUserType({
            userType: newUserType
        });
        window.location.reload();
    };

    const onExitPreview = async () => {
        await ExitPreview();
        if (slug) {
            window.location.href = slug;
        } else {
            window.location.reload();
        }
    };

    const onSharePreview = async () => {
        const params: SharePreviewParams = {
            previewSlug: slug
        };

        try {
            const returned = await SharePreview(params);
            setSharedUrl(returned);
            await navigator.clipboard.writeText(returned);
            alert('Shareable url has been saved to your clipboard!');
        } catch (error) {
            console.error('Error sharing preview:', error);
        }
    };

    useEffect(() => {
        setHasMounted(true);
        setIsInIframe(window.self !== window.top);
    }, []);

    return (
        <>
            <div ref={previewOverlayRef} />

            {hasMounted &&
                previewOverlayRef.current &&
                ReactDOM.createPortal(
                    <Overlay $isVisible={isVisible}>
                        <HideButtonContainer $isVisible={isVisible}>
                            <Button variant="ghost" onClick={() => setIsVisible(!isVisible)}>
                                <Arrow $isVisible={isVisible} type="ChevronLeft" size={16} />
                            </Button>
                        </HideButtonContainer>
                        <Content $isVisible={isVisible}>
                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                <div style={{ fontWeight: 'bold', display: 'block' }}>Notice</div>
                                <span>You are in preview mode</span>
                            </div>
                            <div>
                                <span>Select user type</span>
                                <StyledSelect options={availableOptions} onChange={onChange} value={selectedOption} />
                            </div>
                            {!isInIframe && (
                                <StyledButton variant="secondary" onClick={onExitPreview}>
                                    Exit preview mode
                                </StyledButton>
                            )}
                            {
                                <StyledButton variant="secondary" onClick={onSharePreview}>
                                    Share preview link
                                </StyledButton>
                            }
                            {sharedUrl && isInIframe && (
                                <div style={{ marginTop: '1rem' }}>
                                    <label htmlFor="shared-link">Shareable Link</label>
                                    <input
                                        id="shared-link"
                                        type="text"
                                        value={sharedUrl}
                                        readOnly
                                        style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                                    />
                                </div>
                            )}
                        </Content>
                    </Overlay>,
                    previewOverlayRef.current as Element
                )}
        </>
    );
}
